import React, { useEffect, useState } from 'react';
import JobSurveysDropdown from 'src/components/JobSurveysDropdown/';
import {
  useGetSurveyDataFalsificationSummaryLazy,
  useDataFalsificationRules,
  useGenerateDataFalsificationResultExcel
} from 'src/hooks/job';
import { useGetSurveyResponsesByDatasheetLazy } from 'src/hooks/survey';
import SelectBox, {
  OptionWithDynamicIcon,
  SingleValueWithIcon,
} from 'src/components/SelectBox';
import { saveAs } from 'file-saver';
import { useSetQueryParams as setQueryParams } from 'src/hooks/utils';
import Loader from 'src/components/Loader';
import moment from 'moment';
import { ResponseTypeOptions } from './constants';
import JobSurveyFalsificationSummary from './JobSurveyFalsificationSummary';
import JobSurveyResponses from './JobSurveyResponses';
import s from './JobSurveyFalsification.styles';


const JobSurveyFalsification = props => {
  const { factory, job, surveyDatasheets } = props;

  const [selectedSurvey, setSelectedSurvey] = useState();
  const [selectedSurveyDatasheet, setSelectedSurveyDatasheet] = useState();
  const [selectedSurveyResponses, setSelectedSurveyResponses] = useState();
  const [
    selectedSurveyInvalidResponse,
    setSelectedSurveyInvalidResponse,
  ] = useState();
  const [isDownloading, setIsDownloading] = useState(false);


  const { data: dataFalsificationRulesData } = useDataFalsificationRules({});

  const [
    jobSurveyFalsificationSummaryCallback,
    { data: jobSurveyFalsificationSummaryData },
  ] = useGetSurveyDataFalsificationSummaryLazy({});

  const [
    jobSurveyResponsesByDatasheetCallback,
    { data: jobSurveyResponsesByDatasheetData, refetch },
  ] = useGetSurveyResponsesByDatasheetLazy({});

  const generateDataFalsificationResultExcel = useGenerateDataFalsificationResultExcel();

  const DataFalsificationRulesOptions = dataFalsificationRulesData?.dataFalsificationRules.map(
    v => {
      return {
        value: v.rule,
        label: v.name,
      };
    },
  );

  const dataFalsificationRulesName = Object.assign(
    {},
    ...(dataFalsificationRulesData?.dataFalsificationRules || []).map(dataFalsificationRule => {
      const objKey = {};
      objKey[dataFalsificationRule.rule] = dataFalsificationRule.name;
      return objKey;
    }),
  );

  useEffect(() => {
    if (selectedSurvey) {
      const responsetypes = selectedSurveyResponses?.map(response => {
        return response.value;
      });
      const surveyInvalidResponse = selectedSurveyInvalidResponse?.value;
      jobSurveyResponsesByDatasheetCallback({
        variables: {
          jobId: job.id,
          datasheetVersionId: selectedSurvey.datasheetVersionId,
          responseType: responsetypes,
          invalidFalsificationRule: surveyInvalidResponse,
          limit: 15,
          offset: 0,
        },
      });
    }
  }, [selectedSurvey, selectedSurveyResponses, selectedSurveyInvalidResponse]);

  const onSelectedSurveyChange = survey => {
    if (survey) {
      setSelectedSurvey(survey);
      setSelectedSurveyResponses(null);
      setSelectedSurveyInvalidResponse(null);
      const surveyDatasheet = surveyDatasheets.find(
        surveyDs =>
          surveyDs.currentVersion.survey.datasheetVersionId ===
          survey.datasheetVersionId,
      );
      setSelectedSurveyDatasheet(surveyDatasheet);
      jobSurveyFalsificationSummaryCallback({
        variables: {
          jobId: job.id,
          datasheetVersionId: survey.datasheetVersionId,
        },
      });
      setQueryParams(window.location, {
        page: 1,
        limit: 15,
      });
    }
  };

  const b64toBlob = async (base64, type = 'application/octet-stream') => {
    return fetch(`data:${type};base64,${base64}`).then(res => res.blob());
  };

  const onDownload = async () => {
    if (isDownloading) {
      return;
    }
    try {
      setIsDownloading(true);
      const response = await generateDataFalsificationResultExcel({ jobId: job.id, datasheetVersionId: selectedSurvey.datasheetVersionId });
      const pdfBlob = await b64toBlob(response?.data.generateDataFalsificationExcel, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const filename = `${job.reportNumber}-${selectedSurvey.label}-${moment().format('DDMMYYYY')}.xlsx`;
      await saveAs(pdfBlob, filename);
      setIsDownloading(false);
    } catch(e) {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <s.Row Title>
        <s.Cell label Title>
          {selectedSurvey?.label} at {factory.company.currentVersion.name}
        </s.Cell>
        <s.Cell>
          <JobSurveysDropdown
            datasheets={surveyDatasheets}
            onChange={onSelectedSurveyChange}
          />
        </s.Cell>
      </s.Row>
      <s.Container>
        <s.Row>
          <s.Cell>
            <JobSurveyFalsificationSummary
              jobSurveyFalsificationSummary={jobSurveyFalsificationSummaryData}
            />
          </s.Cell>
        </s.Row>
      </s.Container>
      {selectedSurvey?.datasheetVersionId && (
        <s.Container last>
          <s.Row>
            <s.Cell>
              <s.Label>Responses:</s.Label>
              <SelectBox
                isClearable={true}
                isMulti
                onChange={selectedOption => {
                  setSelectedSurveyResponses(selectedOption.length ? selectedOption : null);
                }}
                options={ResponseTypeOptions}
                value={selectedSurveyResponses}
              />
            </s.Cell>
            <s.Cell>
              <s.Label>Invalid Response:</s.Label>
              <SelectBox
                components={{
                  Option: OptionWithDynamicIcon,
                  SingleValue: SingleValueWithIcon,
                }}
                isClearable={true}
                onChange={selectedOption => {
                  setSelectedSurveyInvalidResponse(selectedOption);
                }}
                options={DataFalsificationRulesOptions}
                value={selectedSurveyInvalidResponse}
              />
            </s.Cell>
            <s.Cell flex-center flex-none>
              <s.DownloadBtn orange solid onClick={() => onDownload()} type="button">
                {isDownloading ? (
                  <Loader withText={false}/>
                ) : (
                  <s.DownloadIcon name="download" />
                )}
              </s.DownloadBtn>
            </s.Cell>
          </s.Row>
          {jobSurveyResponsesByDatasheetData &&
            jobSurveyResponsesByDatasheetData.surveyResponsesPageInfo
              .totalCount > 0 && (
              <s.Row>
                <s.Cell>
                  <JobSurveyResponses
                    dataFalsificationRules={
                      dataFalsificationRulesData?.dataFalsificationRules
                    }
                    dataFalsificationRulesName={dataFalsificationRulesName}
                    jobSurveyResponsesData={jobSurveyResponsesByDatasheetData}
                    surveyDatasheet={selectedSurveyDatasheet}
                    surveyResponsesRefetch={refetch}
                  />
                </s.Cell>
              </s.Row>
            )}
        </s.Container>
      )}
    </>
  );
};

export default React.memo(JobSurveyFalsification);
